import React from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { ChoiceField } from '@ns/plugin-picker-shared';
import { keepPreviousData } from '@tanstack/react-query';

import { selectGroupPage } from './directory-options';
import {
  GROUP_DIRECTORY_SOURCE_KEY,
  groupListOptions,
} from './directory-query';
import type { EntraGroup } from './types';
import { useGroups } from './use-directory';
import {
  type DirectoryPickerSettings,
  useDirectoryPicker,
} from './use-directory-picker';

/** Searches the group collection for one settled term. */
function useGroupSearch(term: string) {
  return useGroups(groupListOptions({ term }), {
    placeholderData: keepPreviousData,
    select: selectGroupPage,
  });
}

const GROUP_PICKER: DirectoryPickerSettings<EntraGroup> = {
  droppedRowsLabel: 'Entra groups',
  noun: 'groups',
  useSearch: useGroupSearch,
};

/**
 * Renders the fixed Microsoft Entra group picker for a scaffolder field.
 *
 * @remarks
 * The raw combobox text stays local until the exact 200 ms debounce settles.
 * Only that settled value reaches the TanStack query key and the Graph request.
 */
export function EntraGroupPickerComponent(
  props: FieldExtensionComponentProps<EntraGroup>,
): React.ReactElement {
  const picker = useDirectoryPicker(GROUP_PICKER);
  const selected = props.formData;
  return (
    <ChoiceField<EntraGroup>
      description={props.schema.description}
      emptyText={picker.emptyText}
      getKey={item => item.id}
      getLabel={item => item.displayName}
      items={picker.items}
      label={props.schema.title ?? props.name}
      onSelect={item => props.onChange(item)}
      remoteSearch={picker.remoteSearch}
      required={props.required}
      selectedKey={typeof selected?.id === 'string' ? selected.id : null}
      selectedLabel={
        typeof selected?.displayName === 'string' ? selected.displayName : null
      }
      sourceKey={GROUP_DIRECTORY_SOURCE_KEY}
      state={picker.state}
    />
  );
}
